import { motion } from 'framer-motion'
import { InlineEditableInput, InlineEditableText, InlineEditableValue } from '@components'
import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'

export const SuggestionCardContainer = styled.div`
  width: 100%;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const SuggestionCardSurface = styled.div<{ $isDragActive?: boolean }>`
  width: 100%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px ${({ $isDragActive }) => ($isDragActive ? 'dashed' : 'solid')}
    ${({ theme, $isDragActive }) =>
      $isDragActive ? theme.colors.primary : theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  display: flex;
  flex-direction: column;
  transition: border-color 140ms ease-out, background-color 140ms ease-out,
    box-shadow 140ms ease-out;
  background: ${({ theme, $isDragActive }) =>
    $isDragActive
      ? `color-mix(in srgb, ${theme.colors.primary} 6%, ${theme.colors.neutralWhite})`
      : theme.colors.neutralWhite};
  box-shadow: ${({ theme, $isDragActive }) =>
    $isDragActive
      ? `0 0 0 2px color-mix(in srgb, ${theme.colors.primary} 20%, transparent)`
      : 'none'};
`

export const SuggestionHeader = styled.div<{ $highlight?: boolean }>`
  background: ${({ $highlight, theme }) => ($highlight ? theme.dashboard.infoTint : 'transparent')};
  transition: background-color 400ms ease-out;
  position: relative;
  width: 100%;
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.lg}rem;
`

export const HeaderMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
`

export const HeaderAmount = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xxl}rem;
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-variant-numeric: tabular-nums;
`

export const HeaderChipRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 400;
  line-height: 1.35;
`

export const HeaderChipDate = styled.span`
  font-weight: 400;
`

export const HeaderStatusIcon = styled.div<{ $isExpense: boolean }>`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isExpense }) => ($isExpense ? '#fbe3a6' : '#c7ffdc')};
  color: ${({ $isExpense }) => ($isExpense ? '#dfae22' : '#39d273')};

  svg {
    width: 1.1rem;
    height: 1.1rem;
    transform: ${({ $isExpense }) => ($isExpense ? 'rotateY(180deg)' : 'none')};
  }
`

/** "Odottaa liitettä" — plain chip-row text with leading clock icon (not a Badge). */
export const RuleWaitingInline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;

  svg {
    flex-shrink: 0;
    width: 1em;
    height: 1em;
  }
`

export const SuggestionContentDivider = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

export const AccrualZone = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md}rem;
  padding-top: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

export const TimelineHeaderRow = styled(motion.div)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const TimelineHeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-height: ${({ theme }) => theme.iconSize.xs + theme.spacing.xs * 2}rem;
`

export const TimelineHeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-height: ${({ theme }) => theme.iconSize.xs + theme.spacing.xs * 2}rem;
`

export const TimelineConnectorWrap = styled(motion.div)`
  flex: 1;
  display: flex;
`

export const TimelineLabel = styled.div<{ $tone: 'accrual' | 'settlement' | 'muted' }>`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  line-height: 1.2;
  margin-top: ${({ theme }) => theme.spacing.xxxs}rem;
  color: ${({ $tone, theme }) =>
    $tone === 'accrual'
      ? theme.colors.primary
      : $tone === 'settlement'
      ? theme.colors.AIPurple
      : theme.colors.iconGray};
`

export const TimelineDot = styled.div<{ $isRight?: boolean; $isMuted?: boolean }>`
  width: ${({ theme }) => theme.fontSize.xs}rem;
  height: ${({ theme }) => theme.fontSize.xs}rem;
  border-radius: 999px;
  border: 2px solid
    ${({ $isRight, $isMuted, theme }) =>
      $isMuted ? theme.colors.iconGray : $isRight ? theme.colors.AIPurple : theme.colors.primary};
  background: ${({ theme }) => theme.colors.neutralWhite};
`

export const TimelineConnector = styled.div`
  flex: 1;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary} 0%,
    ${({ theme }) => theme.colors.AIPurple} 100%
  );
`

export const AccrualGrid = styled(motion.div)<{ $isSplit: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isSplit }) => ($isSplit ? '1fr 1fr' : '1fr')};
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

export const AccrualColumn = styled(motion.div)<{ $isSettlement?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  align-items: ${({ $isSettlement }) => ($isSettlement ? 'flex-end' : 'flex-start')};
  text-align: ${({ $isSettlement }) => ($isSettlement ? 'right' : 'left')};
`

export const AccrualEyebrowRow = styled.div<{ $isSettlement?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-height: ${({ theme }) => theme.iconSize.xs + theme.spacing.xxs * 2}rem;
  justify-content: ${({ $isSettlement }) => ($isSettlement ? 'flex-end' : 'flex-start')};
`

export const SplitHintBadge = styled(motion.button)`
  border: none;
  border-radius: 999px;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  cursor: pointer;

  svg {
    width: ${({ theme }) => theme.fontSize.xxs}rem;
    height: ${({ theme }) => theme.fontSize.xxs}rem;
  }
`

export const SplitInfoBadge = styled.button`
  border: none;
  border-radius: 999px;
  width: ${({ theme }) => theme.iconSize.xs}rem;
  height: ${({ theme }) => theme.iconSize.xs}rem;
  padding: 0;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  line-height: 1;
`

export const Eyebrow = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.iconGray};
  margin-top: ${({ theme }) => theme.spacing.xxxs}rem;
`

export const ValueText = styled.div<{ $emphasis?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: ${({ $emphasis = true }) => ($emphasis ? 600 : 400)};
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-variant-numeric: tabular-nums;
`

export const MutedText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
`

export const HeaderSourceEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.iconGray};
  margin-top: ${({ theme }) => theme.spacing.xxxs}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

export const HeaderCompactIcon = styled.div<{ $isExpense: boolean }>`
  width: 0.8rem;
  flex-shrink: 0;
  margin-right: 0.25rem;
  margin-top: 1px;
  color: ${({ theme }) => theme.colors.iconGray};
  display: inline-flex;
  font-size: 0.8rem;

  svg {
    width: 0.8rem;
    height: 0.8rem;
    transform: ${({ $isExpense }) => ($isExpense ? 'rotateY(180deg)' : 'none')};
  }
`

export const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-shrink: 0;
`

export const UncertaintyPopoverAnchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

export const UncertaintyPopoverButton = styled.button<{ $tone: 'high' | 'mid' | 'low' }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 999px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ $tone, theme }) =>
    $tone === 'high'
      ? theme.colors.success
      : $tone === 'mid'
      ? theme.dashboard.infoDeep
      : theme.colors.warning};

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

export const ConfidenceRingSvg = styled.svg`
  width: 36px;
  height: 36px;
  transform: rotate(-90deg);
`

export const ConfidenceRingLabel = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
`

export const ConfidenceRingTrack = styled.circle`
  fill: none;
  stroke: ${({ theme }) => theme.colors.neutralGray};
`

export const ConfidenceRingValue = styled.circle`
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
`

export const UncertaintyPopoverPanel = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  padding-top: 8px;
  z-index: 40;
  width: min(330px, 80vw);
`

export const UncertaintyPanelCard = styled.div`
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  overflow: hidden;
  text-align: left;
`

export const UncertaintyPanelSection = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

export const UncertaintyPanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const UncertaintyPanelTitleBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`

export const UncertaintyPanelTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const UncertaintyPanelSummary = styled.span<{
  $tone: 'error' | 'review' | 'ready' | 'high' | 'mid' | 'low'
}>`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  color: ${({ $tone, theme }) =>
    $tone === 'high'
      ? theme.colors.success
      : $tone === 'low'
      ? theme.colors.warning
      : $tone === 'error'
      ? theme.dashboard.critical
      : $tone === 'ready'
      ? theme.dashboard.positive
      : theme.dashboard.infoDeep};
`

export const UncertaintyCloseButton = styled.button`
  width: 26px;
  height: 26px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.iconGray};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

export const UncertaintyBasisRow = styled.div`
  display: grid;
  grid-template-columns: 1rem 1fr auto;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: center;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`

export const UncertaintyBasisText = styled.span<{ $used: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.4;
  color: ${({ $used, theme }) => ($used ? theme.colors.metalGray : theme.colors.iconGray)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const UncertaintyBasisLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const UncertaintyCheckList = styled.div`
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem
    ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  flex-direction: column;
`

export const UncertaintyCheckRow = styled.div`
  display: grid;
  grid-template-columns: 1rem 1fr auto;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: start;
  padding: ${({ theme }) => theme.spacing.xs}rem 0;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`

export const UncertaintyCheckButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`

export const UncertaintyCheckTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const UncertaintyCheckStatus = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const UncertaintyConfirmLink = styled.button`
  border: none;
  background: transparent;
  padding: 2px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  white-space: nowrap;
`

export const UncertaintyNeedBox = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.luca.mist};
  border-top: 1px solid ${({ theme }) => theme.luca.mistBorder};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const UncertaintyNeedRow = styled.div`
  display: grid;
  grid-template-columns: 1rem 1fr;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: start;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
`

export const UncertaintyNeedText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const UncertaintyHoverAnchor = styled.span<{ $align?: 'start' | 'end' }>`
  position: relative;
  display: inline-flex;
  max-width: 100%;

  &:hover > .uncertainty-hover-card,
  &:focus-within > .uncertainty-hover-card {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`

export const UncertaintyHoverCard = styled.div<{ $align?: 'start' | 'end' }>`
  position: absolute;
  top: 100%;
  ${({ $align }) => ($align === 'end' ? 'right: 0;' : 'left: 0;')}
  padding-top: 6px;
  z-index: 30;
  width: min(288px, 80vw);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
`

export const UncertaintyHoverBody = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const UncertaintyHoverTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

export const UncertaintyHoverHeading = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const UncertaintyCheckItem = styled.div`
  display: grid;
  grid-template-columns: 0.9rem 1fr;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: start;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`

export const UncertaintyWhy = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const UncertaintyScore = styled.span<{ $tone: 'high' | 'mid' | 'low' }>`
  font-weight: 700;
  color: ${({ $tone, theme }) =>
    $tone === 'high'
      ? theme.colors.success
      : $tone === 'mid'
      ? theme.dashboard.infoDeep
      : theme.colors.warning};
`

export const UncertaintyDocHint = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.45;
`

export const UncertaintyLucaAsk = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem
    ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

export const UncertaintyLucaAskTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.luca.blue};
`

export const UncertaintyLucaAskRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.luca.mist};
  color: ${({ theme }) => theme.luca.deepBlue};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  line-height: 1.35;
  text-align: left;
  text-wrap: pretty;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.luca.tint};
  }

  svg {
    flex-shrink: 0;
    margin-left: auto;
    font-size: ${({ theme }) => theme.fontSize.xxs}rem;
    color: ${({ theme }) => theme.luca.blue};
  }
`

export const UncertaintyHoverActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

export const ReviewHintSlot = styled(UncertaintyHoverAnchor)`
  margin-left: auto;
  cursor: help;
`

export const ReviewHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.dashboard.infoDeep};
  white-space: nowrap;
  cursor: help;

  svg {
    width: 0.6rem;
    height: 0.6rem;
  }
`

export const AccountsEyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px ${({ theme }) => theme.spacing.sm}rem;
  flex-wrap: wrap;
`

export const UncertainAccountName = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: help;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const attentionPulse = (color: string) => keyframes`
  0% {
    box-shadow: 0 0 0 0 ${color}88;
  }
  100% {
    box-shadow: 0 0 0 7px ${color}00;
  }
`

export const AttentionDot = styled.span<{ $pulse?: boolean }>`
  position: relative;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  display: inline-block;
  flex-shrink: 0;
  ${({ $pulse = true, theme }) =>
    $pulse
      ? css`
          animation: ${attentionPulse(theme.colors.primary)} 1.6s ease-out infinite;

          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        `
      : ''}

  &::before {
    content: '';
    position: absolute;
    inset: -10px;
  }
`

export const AccountNameText = styled(MutedText)`
  color: ${({ theme }) => theme.colors.iconGray};
`

export const AccrualDateErrorText = styled(MutedText)`
  color: ${({ theme }) => theme.colors.warning};
`

export const InlineFieldErrorText = styled(MutedText)`
  color: ${({ theme }) => theme.colors.warning};
`

export const GutterSection = styled.div<{ $highlight?: boolean }>`
  background: ${({ $highlight, theme }) => ($highlight ? theme.dashboard.infoTint : 'transparent')};
  transition: background-color 400ms ease-out;
  width: 100%;
  display: grid;
  grid-template-columns: ${({ theme }) => theme.iconSize.xxs}rem 1fr;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  padding-bottom: 0;

  &:last-child {
    padding-bottom: ${({ theme }) => theme.spacing.lg}rem;
  }
`

export const ContactAccountsSection = styled.div`
  width: 100%;
`

export const ContactSectionGutter = styled(GutterSection)`
  padding-top: ${({ theme }) => theme.spacing.xs}rem;
  padding-bottom: ${({ theme }) => theme.spacing.md}rem;
`

export const AccountsSectionGutter = styled(GutterSection)`
  padding-top: 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm}rem;
`

export const TagsSectionGutter = styled(GutterSection)`
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  padding-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

export const RelationsSectionGutter = styled(GutterSection)`
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  padding-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

export const ContactHeaderRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
`

export const ContactSuggestedRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

type ContactSuggestionTone = 'default' | 'ai'

export const ContactSuggestionMeta = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
`

export const ContactNameBadgeRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
`

export const SuggestionContactActionButton = styled.button`
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.metalGray};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  padding: 0;
  line-height: 1.3;
  align-self: flex-start;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

/** Same slot & typography as `SuggestionContactActionButton` (“Käytä ehdotettua kontaktia?”); not interactive. */
export const ContactPendingNewCaption = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  align-self: flex-start;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const ContactPendingNewCaptionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.AIPurple};

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
  }
`

export const ContactEditActionButton = styled(SuggestionContactActionButton)`
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.metalGray};
  padding: 0;
  min-width: 0;
  min-height: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus-visible {
    border-color: transparent;
    color: ${({ theme }) => theme.colors.metalGray};
    opacity: 1;
  }
`

export const ContactValueRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const ContactCreationBadge = styled.span<{ $tone?: ContactSuggestionTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  padding: ${({ theme }) => theme.spacing.xxxs}rem ${({ theme }) => theme.spacing.xs}rem;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $tone = 'default' }) =>
      $tone === 'ai'
        ? `color-mix(in srgb, ${theme.colors.AIPurple} 45%, ${theme.colors.neutralGray})`
        : theme.colors.neutralGray};
  background: ${({ theme, $tone = 'default' }) =>
    $tone === 'ai'
      ? `color-mix(in srgb, ${theme.colors.AIPurple} 10%, ${theme.colors.neutralWhite})`
      : 'transparent'};
  color: ${({ theme, $tone = 'default' }) =>
    $tone === 'ai' ? theme.colors.AIPurple : theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  line-height: 1.2;

  svg {
    color: ${({ theme, $tone = 'default' }) =>
      $tone === 'ai' ? theme.colors.AIPurple : theme.colors.metalGray};
    width: ${({ theme }) => theme.iconSize.xxs}rem;
    height: ${({ theme }) => theme.iconSize.xxs}rem;
  }
`

export const GutterIcon = styled.div`
  color: ${({ theme }) => theme.colors.iconGray};
  display: inline-flex;
  align-items: center;

  svg {
    width: ${({ theme }) => theme.iconSize.xxs}rem;
    height: ${({ theme }) => theme.iconSize.xxs}rem;
  }
`

export const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  /* flex-basis: auto keeps content-sized children visible; flex: 1 alone uses basis 0% */
  & > * {
    flex: 1 1 auto;
    min-width: 0;
  }
`

export const DescriptionValue = styled(InlineEditableValue).attrs({ $minWidth: 150 })``

export const DescriptionText = styled(InlineEditableText)``

export const DescriptionInput = styled(InlineEditableInput).attrs({ $minWidth: 150 })``

export const AccrualDateValue = styled(InlineEditableValue).attrs({ $minWidth: 100 })``

export const AccrualDateText = styled(InlineEditableText)`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
`

export const AccrualDateInput = styled(InlineEditableInput).attrs({ $minWidth: 100 })`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
`

export const AccountRow = styled.div`
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const AccountLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const ContactLink = styled(Link)`
  color: ${({ theme }) => theme.colors.neutralBlack};
  text-decoration: none;

  &:hover,
  &:focus-visible,
  &:active,
  &:visited {
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

export const ContactNameText = styled.div<{ $tone?: ContactSuggestionTone }>`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.3;
  color: ${({ theme, $tone }) =>
    $tone === 'ai' ? theme.colors.AIPurple : theme.colors.neutralBlack};
`

/** Same idea as form `ContactSuggestion` / `AiSuggestion`: status line + suggested name. */
export const ContactInlineSuggestionBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  flex: 1;
  min-width: 0;
  text-align: left;
`

/** Matches `SuggestionContactActionButton` (“Käytä ehdotettua kontaktia?”) with optional leading icon. */
export const ContactInlineSuggestionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const ContactInlineSuggestionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
  }
`

export const ContactInlineSuggestionDisplayName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 400;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.neutralBlack};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const ContactInlineValue = styled(InlineEditableValue).attrs({ $minWidth: 180 })`
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-weight: 400;

  &:focus-visible {
    outline: revert;
    outline-offset: revert;
    box-shadow: none;
  }
`

export const ContactInlineText = styled(InlineEditableText)`
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-weight: 400;
`

export const AccountAmount = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-variant-numeric: tabular-nums;
`

export const UIRowsContainer = styled.div`
  && {
    flex: 0 0 auto;
  }

  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const UIRowItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

export const BadgeRow = styled.div`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
`

export const AccountTagPill = styled.div<{ $uncertain?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xs}rem;
  border: 1px ${({ $uncertain }) => ($uncertain ? 'dashed' : 'solid')}
    ${({ $uncertain, theme }) => ($uncertain ? theme.colors.primary : theme.colors.neutralGray)};
  background-color: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
`

/** Leading icon in account VAT chips (replaces dot); inherits tone color for SVG. */
export const AccountTagLeadIcon = styled.span<{ $tone: 'info' | 'success' | 'review' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  color: ${({ $tone, theme }) =>
    $tone === 'success' ? theme.colors.success : theme.colors.primary};

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
  }
`

/** Single padded block for linkitykset + liitteet (no divider between). */
export const LinksAndAttachmentsZone = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

export const RelationsEyebrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  width: 100%;
`

export const RelationsSubgroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

export const AttachmentsSubgroup = styled.div<{ $highlight?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  background: ${({ $highlight, theme }) => ($highlight ? theme.dashboard.infoTint : 'transparent')};
  transition: background-color 400ms ease-out;
`

export const SuggestionFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

export const SuggestionFooterSection = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
